import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useApiList, useApiOne, useMutation } from "../api/hooks";
import { CrudForm, CrudPage, type FieldSpec } from "../components/CrudPage";
import { HoursEditor } from "../components/HoursEditor";
import { OptionGroups, type ManagedOptionGroup } from "../components/OptionGroups";
import { api } from "../api/client";
import { Badge, Bool, DataTable, fmtDate, Money, Page, Pagination, SearchBox, Toolbar, useConfirm, useToast } from "../components/ui";
import { ServiceAreas } from "../components/ServiceAreas";
import { useConfig } from "../hooks/useConfig";
import type { VendorDetail, VendorRow } from "../api/types";

const vendorFields = (types: { value: string; label: string }[]): FieldSpec[] => [
  { name: "name", label: "Name", required: true },
  { name: "vendor_type_id", label: "Vendor type", type: "select", options: types, required: true },
  { name: "description", label: "Description", type: "textarea" },
  { name: "address", label: "Address" },
  { name: "lat", label: "Latitude", type: "number", required: true },
  { name: "lng", label: "Longitude", type: "number", required: true },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Email" },
  { name: "commission_pct", label: "Platform commission %", type: "number", decimal: true, hint: "Share of the subtotal kept by the platform (02 §7)" },
  { name: "tax_pct", label: "Tax %", type: "number", decimal: true },
  { name: "base_delivery_fee", label: "Base delivery fee", type: "number", decimal: true },
  { name: "delivery_fee", label: "Delivery fee (per km when enabled)", type: "number", decimal: true },
  { name: "charge_per_km", label: "Charge per km", type: "switch" },
  { name: "delivery_range_km", label: "Delivery range (km)", type: "number", decimal: true },
  { name: "min_order", label: "Minimum order", type: "number", decimal: true },
  { name: "prep_time_minutes", label: "Preparation time (min)", type: "number" },
  { name: "pickup", label: "Pickup", type: "switch" },
  { name: "delivery", label: "Delivery", type: "switch" },
  { name: "allow_schedule_order", label: "Scheduled orders", type: "switch" },
  { name: "auto_accept", label: "Auto-accept orders", type: "switch", editOnly: true },
  { name: "is_active", label: "Active", type: "switch", editOnly: true },
  { name: "logo_url", label: "Logo URL", type: "image" },
  { name: "feature_image_url", label: "Cover image URL", type: "image" },
];

export function VendorsPage() {
  const config = useConfig();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [typeId, setTypeId] = useState("");
  const [active, setActive] = useState("");
  const [page, setPage] = useState(1);
  const query = useMemo(
    () => ({ q: q || undefined, vendor_type_id: typeId || undefined, is_active: active === "" ? undefined : active === "1", page, per_page: 25 }),
    [q, typeId, active, page],
  );
  const list = useApiList<VendorRow>("/admin/vendors", query);
  const [creating, setCreating] = useState(false);
  const { run, busy } = useMutation();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const types = (config?.vendor_types ?? []).map((t) => ({ value: t.id, label: t.name }));

  return (
    <Page
      title="Vendors"
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
          + New vendor
        </button>
      }
    >
      <Toolbar>
        <SearchBox value={q} onChange={(v) => (setQ(v), setPage(1))} />
        <select className="input" value={typeId} onChange={(e) => (setTypeId(e.target.value), setPage(1))}>
          <option value="">Type: all</option>
          {types.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <select className="input" value={active} onChange={(e) => (setActive(e.target.value), setPage(1))}>
          <option value="">Active: all</option>
          <option value="1">Active</option>
          <option value="0">Inactive</option>
        </select>
      </Toolbar>
      <DataTable
        columns={[
          { key: "name", label: "Vendor" },
          { key: "vendor_type_name", label: "Type" },
          { key: "address", label: "Address", render: (v) => v.address ?? "—" },
          { key: "is_open", label: "Open", render: (v) => <Badge tone={v.is_open ? "ok" : "neutral"}>{v.is_open ? "open" : "closed"}</Badge> },
          { key: "is_active", label: "Active", render: (v) => <Bool value={v.is_active} /> },
          { key: "rating_avg", label: "Rating", render: (v) => (v.rating_avg ? `★ ${Number(v.rating_avg).toFixed(1)}` : "—") },
          { key: "completed_orders", label: "Orders", align: "right" },
          { key: "commission_pct", label: "Commission", align: "right", render: (v) => `${v.commission_pct}%` },
          {
            key: "_a",
            label: "",
            align: "right",
            render: (v) => (
              <span className="row-actions" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm danger"
                  onClick={async () => {
                    if (!(await confirm(`Delete ${v.name}? Managers lose access; orders are kept.`))) return;
                    const r = await run(`/admin/vendors/${v.id}`, { method: "DELETE" });
                    if (r.ok) {
                      toast("Vendor deleted");
                      list.reload();
                    } else toast(r.error, "err");
                  }}
                >
                  Delete
                </button>
              </span>
            ),
          },
        ]}
        rows={list.data}
        loading={list.loading}
        error={list.error}
        onRowClick={(v) => navigate(`/vendors/${v.id}`)}
      />
      <Pagination page={page} perPage={25} total={list.total} onPage={setPage} />
      {creating && (
        <CrudForm
          title="New vendor"
          fields={vendorFields(types)}
          initial={{}}
          busy={busy}
          onClose={() => setCreating(false)}
          onSubmit={async (values) => {
            const r = await run<VendorRow>("/admin/vendors", { method: "POST", body: values });
            if (r.ok) {
              toast("Vendor created");
              setCreating(false);
              navigate(`/vendors/${r.data.id}`);
            } else toast(r.error, "err");
          }}
        />
      )}
      {dialog}
    </Page>
  );
}

/** Vendor profile for admins: `GET/PATCH /vendor?vendor_id=` plus the
 * manager accounts (users with this `vendor_id`). */
export function VendorDetailPage() {
  const { id = "" } = useParams();
  const config = useConfig();
  const navigate = useNavigate();
  const { data: v, loading, error, reload } = useApiOne<VendorDetail>("/vendor", { vendor_id: id });
  const admin = useApiList<VendorRow>("/admin/vendors", { q: v?.name ?? "", per_page: 5 }, Boolean(v));
  const row = admin.data.find((r) => r.id === id);
  const managers = useApiList<{ id: string; name: string; email: string; role: string; is_active: boolean }>("/admin/users", { vendor_id: id, role: "manager", per_page: 50 });
  const optionGroups = useApiList<ManagedOptionGroup>("/vendor/option-groups", { vendor_id: id });
  const optionChoices = optionGroups.data.flatMap((g) => g.options.map((o) => ({ value: o.id, label: `${g.name}: ${o.name}` })));
  const [editing, setEditing] = useState(false);
  const [editingHours, setEditingHours] = useState(false);
  const { run, busy } = useMutation();
  const toast = useToast();
  const types = (config?.vendor_types ?? []).map((t) => ({ value: t.id, label: t.name }));

  if (error) return <p className="error">{error}</p>;
  if (!v) return <p>{loading ? "Loading…" : ""}</p>;

  return (
    <Page
      title={v.name}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(`/orders?type=all`)}>
            Orders
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>
            Edit
          </button>
        </>
      }
    >
      <div className="grid">
        <section className="card">
          <h2>Profile</h2>
          <dl>
            <dt>Type</dt>
            <dd>{row?.vendor_type_name ?? config?.vendor_types.find((t) => t.id === v.vendor_type_id)?.name ?? "—"}</dd>
            <dt>Status</dt>
            <dd>
              <Badge tone={v.is_open ? "ok" : "neutral"}>{v.is_open ? "open" : "closed"}</Badge> {row && <Bool value={row.is_active} />}
            </dd>
            <dt>Address</dt>
            <dd>
              {v.address ?? "—"} <span className="muted mono">({v.lat.toFixed(5)}, {v.lng.toFixed(5)})</span>
            </dd>
            <dt>Contact</dt>
            <dd>{[v.phone, v.email].filter(Boolean).join(" · ") || "—"}</dd>
            <dt>Commission / tax</dt>
            <dd>
              {row?.commission_pct ?? "…"}% / {v.tax_pct}%
            </dd>
            <dt>Delivery</dt>
            <dd>
              {v.delivery ? `up to ${v.delivery_range_km} km, base ` : "no delivery"}
              {v.delivery && <Money value={v.base_delivery_fee} />}
              {v.delivery && (v.charge_per_km ? <> + <Money value={v.delivery_fee} /> per km</> : <> + <Money value={v.delivery_fee} /></>)}
              {v.pickup && " · pickup"}
            </dd>
            <dt>Orders</dt>
            <dd>
              {row?.completed_orders ?? "…"} completed{row?.rating_avg ? ` · ★ ${Number(row.rating_avg).toFixed(1)}` : ""}
            </dd>
            <dt>Created</dt>
            <dd>{fmtDate(row?.created_at)}</dd>
          </dl>
        </section>
        <section className="card">
          <h2>
            Opening hours{" "}
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditingHours(true)}>
              Edit
            </button>
          </h2>
          {v.hours.length === 0 ? (
            <p className="muted">Always open (no hours set).</p>
          ) : (
            <dl>
              {v.hours.map((h) => (
                <HourRow key={h.weekday} h={h} />
              ))}
            </dl>
          )}
          <h2>Menus &amp; categories</h2>
          <div className="chips">
            {v.menus.map((m) => (
              <span key={m.id} className="chip">
                {m.name}
              </span>
            ))}
            {v.categories.map((c) => (
              <span key={c.id} className="chip">
                {c.name}
              </span>
            ))}
          </div>
        </section>
        <section className="card">
          <h2>Managers</h2>
          <DataTable
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "is_active", label: "Active", render: (u) => <Bool value={u.is_active} /> },
            ]}
            rows={managers.data}
            loading={managers.loading}
            error={managers.error}
            empty="No manager accounts yet"
            onRowClick={(u) => navigate(`/users?q=${encodeURIComponent(u.email)}`)}
          />
          <button type="button" className="btn btn-ghost" onClick={() => navigate(`/users?new=manager&vendor_id=${id}`)}>
            + Add manager
          </button>
        </section>
      </div>
      <section className="card" style={{ marginTop: 24 }}>
        <CrudPage<{
          id: string;
          name: string;
          price: string;
          discount_price: string;
          unit: string | null;
          available_qty: number | null;
          featured: boolean;
          is_active: boolean;
        }>
          title="Products"
          resource="products"
          basePath="/vendor/products"
          listQuery={{ vendor_id: id, include_inactive: true }}
          mutationQuery={{ vendor_id: id }}
          searchable
          perPage={50}
          columns={[
            { key: "name", label: "Product" },
            { key: "price", label: "Price", align: "right", render: (p) => <Money value={p.price} /> },
            { key: "discount_price", label: "Offer", align: "right", render: (p) => (Number(p.discount_price) > 0 ? <Money value={p.discount_price} /> : "—") },
            { key: "unit", label: "Unit", render: (p) => p.unit ?? "—" },
            { key: "available_qty", label: "Stock", align: "right", render: (p) => (p.available_qty === null ? "∞" : p.available_qty) },
            { key: "featured", label: "Featured", render: (p) => <Bool value={p.featured} /> },
            { key: "is_active", label: "Visible", render: (p) => <Bool value={p.is_active} /> },
          ]}
          fields={[
            { name: "name", label: "Name", required: true },
            { name: "description", label: "Description", type: "textarea" },
            { name: "price", label: "Price", type: "number", decimal: true, required: true },
            { name: "discount_price", label: "Offer price", type: "number", decimal: true },
            { name: "unit", label: "Unit (plate, kg…)" },
            { name: "available_qty", label: "Stock (empty = unlimited)", type: "number" },
            { name: "featured", label: "Featured", type: "switch" },
            { name: "deliverable", label: "Available for delivery", type: "switch" },
            { name: "is_active", label: "Visible on the menu", type: "switch" },
            { name: "option_ids", label: "Options (sizes, extras)", type: "multiselect", options: optionChoices, hint: "Manage the groups below" },
          ]}
          loadForEdit={async (row) => {
            const { data } = await api<{ option_groups: { options: { id: string }[] }[] }>(`/vendor/products/${row.id}`, { query: { vendor_id: id } });
            return { ...row, option_ids: data.option_groups.flatMap((g) => g.options.map((o) => o.id)) };
          }}
        />
      </section>
      <section className="card" style={{ marginTop: 24 }}>
        <OptionGroups vendorId={id} />
      </section>
      <section className="card" style={{ marginTop: 24 }}>
        <ServiceAreas vendorId={id} />
      </section>
      {editingHours && (
        <HoursEditor
          vendorId={id}
          hours={v.hours}
          onClose={() => setEditingHours(false)}
          onSaved={() => {
            setEditingHours(false);
            reload();
          }}
        />
      )}
      {editing && (
        <CrudForm
          title={`Edit ${v.name}`}
          fields={vendorFields(types)}
          initial={{ ...(v as unknown as Record<string, unknown>), commission_pct: row?.commission_pct, is_active: row?.is_active ?? true }}
          busy={busy}
          onClose={() => setEditing(false)}
          onSubmit={async (values) => {
            const r = await run("/vendor", { method: "PATCH", body: values, query: { vendor_id: id } });
            if (r.ok) {
              toast("Saved");
              setEditing(false);
              reload();
              admin.reload();
            } else toast(r.error, "err");
          }}
        />
      )}
    </Page>
  );
}

function HourRow({ h }: { h: { weekday: number; open_time: string; close_time: string } }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <>
      <dt>{days[(h.weekday - 1 + 7) % 7]}</dt>
      <dd className="mono">
        {h.open_time} – {h.close_time}
      </dd>
    </>
  );
}
